'use client';

import { ReactNode } from 'react';
import Image, { ImageProps } from 'next/image';

import { useCreateMediaFilter } from '@/hooks/useCreateMediaFilter';
import { cn } from '@/lib/utils';
import getMediaFilterUrl, {
  FilterableMedia,
  getMediaFilterSrc,
} from '@/utils/get-media-filter';

// createFilterName is the filter generated through the backend when none of
// filterName exists yet. It defaults to filterName when that is a single name.
type FilterProps =
  | { filterName: string; createFilterName?: string }
  | { filterName: string[]; createFilterName: string };

type Props = Omit<ImageProps, 'src'> &
  FilterProps & {
    media: FilterableMedia | null | undefined;
    createEnabled?: boolean;
    // Static image used when there is no media file to show at all.
    fallbackSrc?: string;
    // Rendered while the filter is being generated. Defaults to a gray box.
    pendingFallback?: ReactNode;
  };

/**
 * Image of a backend media that needs a thumbnail filter. Thumbnails are only
 * generated on demand (the legacy site did it on the first request), so when
 * the filter does not exist yet it is created and the image switches to the
 * returned url on this same visit.
 */
export default function MediaFilterImage({
  media,
  filterName,
  createFilterName,
  createEnabled = true,
  pendingFallback,
  fallbackSrc,
  ...imageProps
}: Props) {
  const filterToCreate =
    createFilterName ?? (typeof filterName === 'string' ? filterName : '');

  const existingUrl = getMediaFilterUrl({
    mediaFilters: media?.mediaFilters,
    filterName,
  });

  // Same url the page used before: keeps the old behavior when creation fails.
  const guessedUrl = getMediaFilterSrc({
    media: media && { path: media.path, filename: media.filename },
    filterName: filterToCreate,
  });

  const { createdUrl, failed } = useCreateMediaFilter({
    imageUrl: existingUrl,
    // Nothing to generate the thumb from without the original file.
    mediaId: guessedUrl ? media?.id : undefined,
    filterName: filterToCreate,
    enabled: createEnabled,
  });

  // The filter can still be created: now, or once createEnabled turns on.
  const isAwaitingFilter =
    !existingUrl &&
    !createdUrl &&
    !failed &&
    media?.id !== undefined &&
    guessedUrl !== undefined;
  const isCreating = isAwaitingFilter && createEnabled;

  // Without an id nothing can be created, so the guessed url is all there is.
  const src =
    existingUrl ??
    createdUrl ??
    (isAwaitingFilter ? undefined : (guessedUrl ?? fallbackSrc));

  if (src) {
    return <Image {...imageProps} alt={imageProps.alt} src={src} />;
  }

  if (isAwaitingFilter && pendingFallback !== undefined) {
    return <>{pendingFallback}</>;
  }

  const { fill, width, height, className } = imageProps;

  return (
    <div
      aria-hidden="true"
      className={cn(
        'bg-gray-100',
        isCreating && 'animate-pulse',
        fill ? 'absolute inset-0' : 'w-full',
        className,
      )}
      style={
        !fill && width && height
          ? { aspectRatio: `${width} / ${height}` }
          : undefined
      }
    />
  );
}
